import { useCallback, useEffect, useRef, useState } from "react";
import { id } from "@suite/core";
import {
  LARGE_FILE_BYTES,
  makePageRefs,
  type FieldValue,
  type LoadedDoc,
  type PageRef,
  type Stamp,
} from "./document.ts";
import type {
  CloseRequest,
  PageMessage,
  PageRequest,
  RenderRequest,
  WorkerMessage,
} from "./pdfWorker.ts";

export interface LoadState {
  loading: boolean;
  error: string | null;
}

export interface PageRender {
  bitmap: ImageBitmap;
  width: number;
  height: number;
}

const THUMB_MAX_EDGE = 260;

export function usePdfPages() {
  const [docs, setDocs] = useState<Map<string, LoadedDoc>>(new Map());
  const [pages, setPages] = useState<PageRef[]>([]);
  const [state, setState] = useState<LoadState>({ loading: false, error: null });

  const workerRef = useRef<Worker | null>(null);
  const pendingRef = useRef(0);
  const requestSeq = useRef(0);
  const pageWaiters = useRef(
    new Map<number, { resolve: (r: PageRender) => void; reject: (e: Error) => void }>(),
  );

  const worker = useCallback(() => {
    if (!workerRef.current) {
      workerRef.current = new Worker(new URL("./pdfWorker.ts", import.meta.url), {
        type: "module",
      });
      workerRef.current.onmessage = (event: MessageEvent<WorkerMessage>) => {
        const message = event.data;
        if (message.type === "thumb") {
          setPages((prev) =>
            prev.map((page) =>
              page.docId === message.docId && page.sourcePage === message.pageNumber
                ? {
                    ...page,
                    bitmap: message.bitmap,
                    width: message.width,
                    height: message.height,
                    intrinsicRotation: message.rotation,
                    view: message.view,
                  }
                : page,
            ),
          );
        } else if (message.type === "fields") {
          setDocs((prev) => {
            const doc = prev.get(message.docId);
            if (!doc) return prev;
            return new Map(prev).set(message.docId, { ...doc, fields: message.fields });
          });
        } else if (message.type === "page") {
          settlePage(message);
        } else if (message.type === "error") {
          setState({ loading: false, error: message.message });
          // A failed document must not stay in the page list half-loaded.
          setPages((prev) => prev.filter((p) => p.docId !== message.docId));
          setDocs((prev) => {
            const next = new Map(prev);
            next.delete(message.docId);
            return next;
          });
          pendingRef.current = Math.max(0, pendingRef.current - 1);
        } else if (message.type === "done") {
          pendingRef.current = Math.max(0, pendingRef.current - 1);
          if (pendingRef.current === 0) {
            setState((s) => ({ ...s, loading: false }));
          }
        }
      };
      workerRef.current.onerror = () =>
        setState({ loading: false, error: "The PDF renderer failed to start." });
    }
    return workerRef.current;
  }, []);

  const settlePage = (message: PageMessage) => {
    const waiter = pageWaiters.current.get(message.requestId);
    if (!waiter) {
      message.bitmap?.close();
      return;
    }
    pageWaiters.current.delete(message.requestId);
    if (message.bitmap) {
      waiter.resolve({ bitmap: message.bitmap, width: message.width, height: message.height });
    } else {
      waiter.reject(new Error(message.error ?? "Page could not be rendered"));
    }
  };

  useEffect(
    () => () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    },
    [],
  );

  const addFiles = useCallback(
    async (files: File[]) => {
      const pdfs = files.filter(
        (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name),
      );
      if (pdfs.length === 0) {
        setState({ loading: false, error: "Those files are not PDFs." });
        return;
      }

      setState({ loading: true, error: null });

      for (const file of pdfs) {
        if (file.size > LARGE_FILE_BYTES) {
          setState({
            loading: false,
            error: `${file.name} is ${Math.round(file.size / 1024 / 1024)} MB. Files over ${
              LARGE_FILE_BYTES / 1024 / 1024
            } MB may exhaust memory in the browser, especially on phones.`,
          });
          continue;
        }

        const buffer = await file.arrayBuffer();
        const docId = id("doc");

        // Two copies on purpose: one is transferred to the worker (and becomes
        // unusable here), the other is kept for building the output.
        const kept = new Uint8Array(buffer.slice(0));

        const doc: LoadedDoc = {
          id: docId,
          name: file.name,
          size: file.size,
          pageCount: 0,
          bytes: kept,
          fields: [],
          fills: {},
        };

        // Page count comes from the worker's meta message; until then the doc
        // has no pages, so ask the worker first and add refs on reply.
        const w = worker();
        pendingRef.current += 1;

        const onMeta = (event: MessageEvent<WorkerMessage>) => {
          const message = event.data;
          if (message.type !== "meta" || message.docId !== docId) return;
          w.removeEventListener("message", onMeta);
          const withCount = { ...doc, pageCount: message.pageCount };
          setDocs((prev) => new Map(prev).set(docId, withCount));
          setPages((prev) => [...prev, ...makePageRefs(withCount)]);
        };
        w.addEventListener("message", onMeta);

        const request: RenderRequest = {
          type: "render",
          docId,
          data: buffer,
          pageNumbers: [],
          maxEdge: THUMB_MAX_EDGE,
        };
        w.postMessage(request, [buffer]);
      }
    },
    [worker],
  );

  /** Full-size render of one page for the edit view; the caller owns the bitmap. */
  const renderPage = useCallback(
    (docId: string, pageNumber: number, rotation: number, width: number) =>
      new Promise<PageRender>((resolve, reject) => {
        const requestId = ++requestSeq.current;
        pageWaiters.current.set(requestId, { resolve, reject });
        const request: PageRequest = { type: "page", requestId, docId, pageNumber, rotation, width };
        worker().postMessage(request);
      }),
    [worker],
  );

  const reset = useCallback(() => {
    // ImageBitmaps hold GPU memory that GC won't reclaim promptly.
    setPages((prev) => {
      for (const page of prev) page.bitmap?.close();
      return [];
    });
    setDocs(new Map());
    setState({ loading: false, error: null });
    for (const waiter of pageWaiters.current.values()) waiter.reject(new Error("Cleared"));
    pageWaiters.current.clear();
    if (workerRef.current) {
      const request: CloseRequest = { type: "close" };
      workerRef.current.postMessage(request);
    }
  }, []);

  const clearError = useCallback(
    () => setState((s) => ({ ...s, error: null })),
    [],
  );

  // ---- editing ------------------------------------------------------------

  const setFill = useCallback((docId: string, name: string, value: FieldValue) => {
    setDocs((prev) => {
      const doc = prev.get(docId);
      if (!doc) return prev;
      return new Map(prev).set(docId, { ...doc, fills: { ...doc.fills, [name]: value } });
    });
  }, []);

  const clearFills = useCallback((docId?: string) => {
    setDocs((prev) => {
      const next = new Map(prev);
      for (const [key, doc] of next) {
        if (docId && key !== docId) continue;
        next.set(key, { ...doc, fills: {} });
      }
      return next;
    });
  }, []);

  const addStamp = useCallback((pageId: string, stamp: Stamp) => {
    setPages((prev) =>
      prev.map((p) => (p.id === pageId ? { ...p, stamps: [...p.stamps, stamp] } : p)),
    );
  }, []);

  const updateStamp = useCallback((pageId: string, stampId: string, patch: Partial<Stamp>) => {
    setPages((prev) =>
      prev.map((p) =>
        p.id === pageId
          ? { ...p, stamps: p.stamps.map((s) => (s.id === stampId ? { ...s, ...patch } : s)) }
          : p,
      ),
    );
  }, []);

  const removeStamp = useCallback((pageId: string, stampId: string) => {
    setPages((prev) =>
      prev.map((p) =>
        p.id === pageId ? { ...p, stamps: p.stamps.filter((s) => s.id !== stampId) } : p,
      ),
    );
  }, []);

  return {
    docs,
    pages,
    setPages,
    state,
    addFiles,
    reset,
    clearError,
    renderPage,
    setFill,
    clearFills,
    addStamp,
    updateStamp,
    removeStamp,
  };
}
